import {
    Boxes,
    Camera,
    Crop,
    FileUp,
    Palette,
    Plus,
    Share2,
    Spline,
    Trash2,
} from "lucide-react";
import { useGridCubes3d } from "./GridCubes3dContext";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { CheckboxField } from "../reusable/CheckboxField";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { ColorPaletteEditor } from "../reusable/ColorPaletteEditor";
import { PresetSelector } from "../reusable/PresetSelector";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { isValidSvg } from "../../utils/svgUtils";
import { roundDeg } from "../../utils/primitivePointsPlacement";

export function GridCubes3dControls() {
    const {
        gridX,
        gridY,
        gridZ,
        cellSize,
        density,
        populationMode,
        setPopulationMode,
        maxObjectCells,
        seed,
        connectionsEnabled,
        connectionsClipToMask,
        connectionDensity,
        connectionMode,
        connectionStyleMode,
        connectionLineStyle,
        connectionLineStylePathfinding,
        connectionLineStyleDirect,
        connectionStrandCount,
        connectionStrandSpacing,
        connectionCurved,
        connectionArrowMode,
        segmentSubdivisions,
        connectionColor,
        connectionColorAlt,
        connectionWidth,
        connectionWidthAlt,
        setConnectionStyleMode,
        setConnectionLineStyle,
        setConnectionLineStylePathfinding,
        setConnectionLineStyleDirect,
        setConnectionStrandCount,
        setConnectionStrandSpacing,
        setConnectionCurved,
        setConnectionArrowMode,
        setConnectionColorAlt,
        setConnectionWidthAlt,
        rotationXDeg,
        rotationYDeg,
        rotationZDeg,
        cameraType,
        cameraFovDeg,
        cameraDistance,
        viewBoxX,
        viewBoxY,
        viewBoxWidth,
        viewBoxHeight,
        backfaceCulling,
        palette,
        ambient,
        maskEnabled,
        maskScale,
        maskOffsetXPct,
        maskOffsetYPct,
        maskPreviewEnabled,
        maskSvgs,
        maskInputRefs,
        faceStrokeEnabled,
        faceStrokeColor,
        faceStrokeWidth,
        error,
        setGridX,
        setGridY,
        setGridZ,
        setCellSize,
        setDensity,
        setMaxObjectCells,
        setSeed,
        setConnectionsEnabled,
        setConnectionsClipToMask,
        setConnectionDensity,
        setConnectionMode,
        setSegmentSubdivisions,
        setConnectionColor,
        setConnectionWidth,
        setRotationXDeg,
        setRotationYDeg,
        setRotationZDeg,
        setCameraType,
        setCameraFovDeg,
        setCameraDistance,
        setViewBoxX,
        setViewBoxY,
        setViewBoxWidth,
        setViewBoxHeight,
        setBackfaceCulling,
        setPalette,
        setAmbient,
        setMaskEnabled,
        setMaskScale,
        setMaskOffsetXPct,
        setMaskOffsetYPct,
        setMaskPreviewEnabled,
        addMaskSvg,
        removeMaskSvg,
        handleMaskFileLoad,
        setFaceStrokeEnabled,
        setFaceStrokeColor,
        setFaceStrokeWidth,
        canExport,
        buildExportSvgString,
        applyPreset,
        getPresetValues,
        resetParameters,
    } = useGridCubes3d();

    const handleInt =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(Math.round(n));
        };

    const handleFloat =
        (setter: (v: number) => void, min = 0, max = Infinity) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(n);
        };

    const handleRotationDeg =
        (setter: (v: number) => void, min = -3600, max = 3600) =>
        (v: string | number) => {
            const n = typeof v === "number" ? v : Number(v);
            if (!Number.isFinite(n) || n < min || n > max) return;
            setter(roundDeg(n));
        };

    const importMaskFiles = (files: File[] | FileList) => {
        for (const file of Array.from(files)) {
            const reader = new FileReader();
            reader.onload = () => {
                const text =
                    typeof reader.result === "string" ? reader.result : "";
                if (!text || !isValidSvg(text)) return;
                addMaskSvg({ name: file.name, content: text });
            };
            reader.readAsText(file);
        }
    };

    return (
        <div className="panel-controls path-populate-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Grid Cubes 3D</h2>
                    <ToolDescriptionPopover dialogLabel="Grid Cubes 3D tool description">
                        <p>
                            Populate a 3D grid with box objects by density, connect
                            random pairs with grid-routed or direct lines, then project
                            the scene through a draggable camera into a depth-sorted SVG.
                            Drag the canvas to orbit.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                </div>
            </div>

            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="grid-cubes-3d"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />

                <CollapsibleToolGroup title="Grid" icon={Boxes}>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Grid X
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={32}
                                step={1}
                                value={gridX}
                                onChange={handleInt(setGridX, 1, 32)}
                            />
                        </label>
                        <label className="field-label">
                            Grid Y
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={32}
                                step={1}
                                value={gridY}
                                onChange={handleInt(setGridY, 1, 32)}
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Grid Z
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={32}
                                step={1}
                                value={gridZ}
                                onChange={handleInt(setGridZ, 1, 32)}
                            />
                        </label>
                        <label className="field-label">
                            Cell size
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                max={100}
                                step={0.1}
                                value={cellSize}
                                onChange={handleFloat(setCellSize, 0.1, 100)}
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Density
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.01}
                                value={density}
                                onChange={handleFloat(setDensity, 0, 1)}
                            />
                        </label>
                        <label className="field-label">
                            Distribution
                            <select
                                value={populationMode}
                                onChange={(e) =>
                                    setPopulationMode(
                                        e.target.value as
                                            | "uniform"
                                            | "center"
                                            | "edges"
                                            | "bottom"
                                            | "top",
                                    )
                                }
                                className="anchor-select"
                            >
                                <option value="uniform">Uniform</option>
                                <option value="center">Center</option>
                                <option value="edges">Edges</option>
                                <option value="bottom">Bottom</option>
                                <option value="top">Top</option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Max object size (cells)
                            <DebouncedInput
                                type="number"
                                min={1}
                                max={8}
                                step={1}
                                value={maxObjectCells}
                                onChange={handleInt(setMaxObjectCells, 1, 8)}
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Seed
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={1}
                                value={seed}
                                onChange={handleInt(setSeed, 0, 1_000_000)}
                            />
                        </label>
                        <button
                            type="button"
                            className="generic-button"
                            style={{ alignSelf: "end" }}
                            onClick={() =>
                                setSeed(Math.floor(Math.random() * 1_000_000))
                            }
                        >
                            Randomize
                        </button>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Mask" icon={Crop}>
                    <p className="path-tool-meta" style={{ margin: "0 0 0.5rem" }}>
                        Imported SVGs act as a screen-space mask: objects are kept
                        only when their entire box projects inside a filled mask
                        area. Masks union together and follow the camera as you
                        orbit.
                    </p>
                    <div className="field-row">
                        <CheckboxField
                            checked={maskEnabled}
                            onChange={setMaskEnabled}
                            label="Use SVG mask"
                            inline
                        />
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={maskPreviewEnabled}
                            onChange={setMaskPreviewEnabled}
                            label="Show mask area preview"
                            title="Overlay allowed regions on the canvas (filled mask areas)"
                            disabled={maskSvgs.length === 0}
                            inline
                        />
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Mask scale
                            <DebouncedInput
                                type="number"
                                min={0.05}
                                max={8}
                                step={0.05}
                                value={maskScale}
                                onChange={handleFloat(setMaskScale, 0.05, 8)}
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Mask offset X (%)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={maskOffsetXPct}
                                onChange={handleFloat(setMaskOffsetXPct, 0, 100)}
                            />
                        </label>
                        <label className="field-label">
                            Mask offset Y (%)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={100}
                                step={1}
                                value={maskOffsetYPct}
                                onChange={handleFloat(setMaskOffsetYPct, 0, 100)}
                            />
                        </label>
                    </div>
                    <p className="path-tool-meta" style={{ margin: "0 0 0.5rem" }}>
                        Scale and offset apply in screen space (50% = centered).
                        Preview updates live; press Generate to apply to objects.
                    </p>
                    <div className="path-populate-object-rows">
                        {maskSvgs.map((svg, index) => (
                            <div key={index} className="path-populate-object-row">
                                <div className="upload-controls">
                                    <button
                                        type="button"
                                        className="tool-file-btn tool-file-btn--stretch"
                                        onClick={() =>
                                            maskInputRefs.current[index]?.click()
                                        }
                                    >
                                        <FileUp
                                            className="tool-file-btn__icon"
                                            size={18}
                                            aria-hidden
                                        />
                                        <span>
                                            {svg.name || `Choose mask SVG ${index + 1}...`}
                                        </span>
                                    </button>
                                    <input
                                        ref={(el) => {
                                            if (el) maskInputRefs.current[index] = el;
                                        }}
                                        type="file"
                                        accept=".svg,image/svg+xml"
                                        className="upload-input"
                                        onChange={handleMaskFileLoad(index)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="tool-action-btn tool-action-btn--danger"
                                    onClick={() => removeMaskSvg(index)}
                                >
                                    <Trash2
                                        className="tool-action-btn__icon"
                                        size={16}
                                        aria-hidden
                                    />
                                    <span>Remove</span>
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            className="tool-action-btn tool-action-btn--add"
                            onClick={() => {
                                const input = document.createElement("input");
                                input.type = "file";
                                input.multiple = true;
                                input.accept = ".svg,image/svg+xml";
                                input.style.display = "none";
                                input.addEventListener("change", (e) => {
                                    importMaskFiles(
                                        (e.target as HTMLInputElement).files ?? [],
                                    );
                                    document.body.removeChild(input);
                                });
                                document.body.appendChild(input);
                                input.click();
                            }}
                            onDragOver={(e) => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = "copy";
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                importMaskFiles(e.dataTransfer.files);
                            }}
                        >
                            <Plus
                                className="tool-action-btn__icon"
                                size={18}
                                strokeWidth={2.25}
                                aria-hidden
                            />
                            <span>Add mask SVG</span>
                        </button>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Connections" icon={Spline}>
                    <div className="field-row">
                        <CheckboxField
                            checked={connectionsEnabled}
                            onChange={setConnectionsEnabled}
                            label="Enable connections"
                            inline
                        />
                    </div>
                    {connectionsEnabled && (
                        <>
                            <div className="field-row">
                                <CheckboxField
                                    checked={connectionsClipToMask}
                                    onChange={setConnectionsClipToMask}
                                    label="Clip connections to mask"
                                    title="Pathfinding only routes through in-mask cells; direct lines drop segments outside the mask"
                                    disabled={!maskEnabled || maskSvgs.length === 0}
                                    inline
                                />
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Mode
                                    <select
                                        value={connectionMode}
                                        onChange={(e) =>
                                            setConnectionMode(
                                                e.target.value as
                                                    | "pathfinding"
                                                    | "direct",
                                            )
                                        }
                                        className="anchor-select"
                                    >
                                        <option value="pathfinding">
                                            Pathfinding (90° bends)
                                        </option>
                                        <option value="direct">
                                            Direct lines
                                        </option>
                                    </select>
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Connection density
                                    <DebouncedInput
                                        type="number"
                                        min={0}
                                        max={2}
                                        step={0.05}
                                        value={connectionDensity}
                                        onChange={handleFloat(
                                            setConnectionDensity,
                                            0,
                                            2,
                                        )}
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Segment subdivisions
                                    <DebouncedInput
                                        type="number"
                                        min={1}
                                        max={32}
                                        step={1}
                                        value={segmentSubdivisions}
                                        onChange={handleInt(
                                            setSegmentSubdivisions,
                                            1,
                                            32,
                                        )}
                                    />
                                </label>
                            </div>
                            <div className="field-row">
                                <label className="field-label">
                                    Line appearance
                                    <select
                                        value={connectionStyleMode}
                                        onChange={(e) =>
                                            setConnectionStyleMode(
                                                e.target.value as
                                                    | "uniform"
                                                    | "random"
                                                    | "byRouting",
                                            )
                                        }
                                        className="anchor-select"
                                    >
                                        <option value="uniform">Uniform</option>
                                        <option value="random">
                                            Random per connection
                                        </option>
                                        <option value="byRouting">
                                            By routing mode
                                        </option>
                                    </select>
                                </label>
                            </div>
                            {connectionStyleMode === "uniform" && (
                                <div className="field-row">
                                    <label className="field-label">
                                        Line style
                                        <select
                                            value={connectionLineStyle}
                                            onChange={(e) =>
                                                setConnectionLineStyle(
                                                    e.target.value as
                                                        | "solid"
                                                        | "dashed"
                                                        | "dotted",
                                                )
                                            }
                                            className="anchor-select"
                                        >
                                            <option value="solid">Solid</option>
                                            <option value="dashed">
                                                Dashed
                                            </option>
                                            <option value="dotted">
                                                Dotted
                                            </option>
                                        </select>
                                    </label>
                                </div>
                            )}
                            {connectionStyleMode === "byRouting" && (
                                <>
                                    <div className="field-row">
                                        <label className="field-label">
                                            Pathfinding style
                                            <select
                                                value={
                                                    connectionLineStylePathfinding
                                                }
                                                onChange={(e) =>
                                                    setConnectionLineStylePathfinding(
                                                        e.target.value as
                                                            | "solid"
                                                            | "dashed"
                                                            | "dotted",
                                                    )
                                                }
                                                className="anchor-select"
                                            >
                                                <option value="solid">
                                                    Solid
                                                </option>
                                                <option value="dashed">
                                                    Dashed
                                                </option>
                                                <option value="dotted">
                                                    Dotted
                                                </option>
                                            </select>
                                        </label>
                                    </div>
                                    <div className="field-row">
                                        <label className="field-label">
                                            Direct style
                                            <select
                                                value={
                                                    connectionLineStyleDirect
                                                }
                                                onChange={(e) =>
                                                    setConnectionLineStyleDirect(
                                                        e.target.value as
                                                            | "solid"
                                                            | "dashed"
                                                            | "dotted",
                                                    )
                                                }
                                                className="anchor-select"
                                            >
                                                <option value="solid">
                                                    Solid
                                                </option>
                                                <option value="dashed">
                                                    Dashed
                                                </option>
                                                <option value="dotted">
                                                    Dotted
                                                </option>
                                            </select>
                                        </label>
                                    </div>
                                </>
                            )}
                            <div className="field-row field-row--two">
                                <label className="field-label">
                                    Line color
                                    <input
                                        type="color"
                                        value={connectionColor}
                                        onChange={(e) =>
                                            setConnectionColor(e.target.value)
                                        }
                                    />
                                </label>
                                <label className="field-label">
                                    Alt color
                                    <input
                                        type="color"
                                        value={connectionColorAlt}
                                        onChange={(e) =>
                                            setConnectionColorAlt(
                                                e.target.value,
                                            )
                                        }
                                        disabled={
                                            connectionStyleMode !== "random"
                                        }
                                    />
                                </label>
                            </div>
                            <div className="field-row field-row--two">
                                <label className="field-label">
                                    Line width
                                    <DebouncedInput
                                        type="number"
                                        min={0.1}
                                        max={20}
                                        step={0.1}
                                        value={connectionWidth}
                                        onChange={handleFloat(
                                            setConnectionWidth,
                                            0.1,
                                            20,
                                        )}
                                    />
                                </label>
                                <label className="field-label">
                                    Alt width
                                    <DebouncedInput
                                        type="number"
                                        min={0.1}
                                        max={20}
                                        step={0.1}
                                        value={connectionWidthAlt}
                                        onChange={handleFloat(
                                            setConnectionWidthAlt,
                                            0.1,
                                            20,
                                        )}
                                        disabled={
                                            connectionStyleMode !== "random"
                                        }
                                    />
                                </label>
                            </div>
                            <div className="field-row field-row--two">
                                <label className="field-label">
                                    Parallel strands
                                    <DebouncedInput
                                        type="number"
                                        min={1}
                                        max={8}
                                        step={1}
                                        value={connectionStrandCount}
                                        onChange={handleInt(
                                            setConnectionStrandCount,
                                            1,
                                            8,
                                        )}
                                    />
                                </label>
                                <label className="field-label">
                                    Strand spacing
                                    <DebouncedInput
                                        type="number"
                                        min={0}
                                        max={40}
                                        step={0.5}
                                        value={connectionStrandSpacing}
                                        onChange={handleFloat(
                                            setConnectionStrandSpacing,
                                            0,
                                            40,
                                        )}
                                        disabled={connectionStrandCount <= 1}
                                    />
                                </label>
                            </div>
                            <CheckboxField
                                label="Smooth curved paths"
                                checked={connectionCurved}
                                onChange={setConnectionCurved}
                            />
                            <div className="field-row">
                                <label className="field-label">
                                    Arrowheads
                                    <select
                                        value={connectionArrowMode}
                                        onChange={(e) =>
                                            setConnectionArrowMode(
                                                e.target.value as
                                                    | "none"
                                                    | "end"
                                                    | "both"
                                                    | "random",
                                            )
                                        }
                                        className="anchor-select"
                                    >
                                        <option value="none">None</option>
                                        <option value="end">End only</option>
                                        <option value="both">Both ends</option>
                                        <option value="random">
                                            Random per connection
                                        </option>
                                    </select>
                                </label>
                            </div>
                        </>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Camera" icon={Camera}>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Rotate X (deg)
                            <DebouncedInput
                                type="number"
                                step={0.01}
                                value={rotationXDeg}
                                onChange={handleRotationDeg(setRotationXDeg)}
                            />
                        </label>
                        <label className="field-label">
                            Rotate Y (deg)
                            <DebouncedInput
                                type="number"
                                step={0.01}
                                value={rotationYDeg}
                                onChange={handleRotationDeg(setRotationYDeg)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Rotate Z (deg)
                            <DebouncedInput
                                type="number"
                                step={0.01}
                                value={rotationZDeg}
                                onChange={handleRotationDeg(setRotationZDeg)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <label className="field-label">
                            Projection
                            <select
                                value={cameraType}
                                onChange={(e) =>
                                    setCameraType(
                                        e.target.value as
                                            | "perspective"
                                            | "orthographic",
                                    )
                                }
                                className="anchor-select"
                            >
                                <option value="perspective">Perspective</option>
                                <option value="orthographic">
                                    Orthographic
                                </option>
                            </select>
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            Camera FOV (deg)
                            <DebouncedInput
                                type="number"
                                min={5}
                                max={170}
                                step={1}
                                value={cameraFovDeg}
                                onChange={handleFloat(setCameraFovDeg, 5, 170)}
                            />
                        </label>
                        <label className="field-label">
                            Camera distance
                            <DebouncedInput
                                type="number"
                                min={0.1}
                                max={100}
                                step={0.5}
                                value={cameraDistance}
                                onChange={handleFloat(setCameraDistance, 0.1, 100)}
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            ViewBox X
                            <DebouncedInput
                                type="number"
                                step={1}
                                value={viewBoxX}
                                onChange={handleFloat(setViewBoxX, -100000, 100000)}
                            />
                        </label>
                        <label className="field-label">
                            ViewBox Y
                            <DebouncedInput
                                type="number"
                                step={1}
                                value={viewBoxY}
                                onChange={handleFloat(setViewBoxY, -100000, 100000)}
                            />
                        </label>
                    </div>
                    <div className="field-row field-row--two">
                        <label className="field-label">
                            ViewBox Width
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={viewBoxWidth}
                                onChange={handleFloat(setViewBoxWidth, 1, 100000)}
                            />
                        </label>
                        <label className="field-label">
                            ViewBox Height
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={viewBoxHeight}
                                onChange={handleFloat(setViewBoxHeight, 1, 100000)}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Appearance" icon={Palette}>
                    <p className="path-tool-meta" style={{ margin: "0 0 0.35rem" }}>
                        Face palette (dark → light by orientation)
                    </p>
                    <ColorPaletteEditor
                        colors={palette}
                        onChange={setPalette}
                        minColors={1}
                        maxColors={8}
                        defaultNewColor="#888888"
                    />
                    <div className="field-row">
                        <label className="field-label">
                            Ambient light
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.05}
                                value={ambient}
                                onChange={handleFloat(setAmbient, 0, 1)}
                            />
                        </label>
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={backfaceCulling}
                            onChange={setBackfaceCulling}
                            label="Backface culling"
                            inline
                        />
                    </div>
                    <div className="field-row">
                        <CheckboxField
                            checked={faceStrokeEnabled}
                            onChange={setFaceStrokeEnabled}
                            label="Outline faces"
                            inline
                        />
                    </div>
                    {faceStrokeEnabled && (
                        <div className="field-row field-row--two">
                            <label className="field-label">
                                Outline color
                                <input
                                    type="color"
                                    value={faceStrokeColor}
                                    onChange={(e) =>
                                        setFaceStrokeColor(e.target.value)
                                    }
                                />
                            </label>
                            <label className="field-label">
                                Outline width
                                <DebouncedInput
                                    type="number"
                                    min={0}
                                    max={20}
                                    step={0.1}
                                    value={faceStrokeWidth}
                                    onChange={handleFloat(
                                        setFaceStrokeWidth,
                                        0,
                                        20,
                                    )}
                                />
                            </label>
                        </div>
                    )}
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={!canExport}
                        getSvg={() => ({
                            name: "grid-cubes-3d-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["grid-cubes-3d"]}
                    />
                </CollapsibleToolGroup>
            </div>

            {error && <p className="upload-error">{error}</p>}
        </div>
    );
}
